import React, { useContext, useMemo } from 'react';
import Head from 'next/head';
import UserContext from '../../common/utils/getContext';
import { useRouter } from 'next/router';
import { SITE_URL } from '../../common/utils/utils';
import PropTypes from 'prop-types';
import { getLang, getRoute } from '../../common/utils/router';
const project = require('../../scripts/configs/project');

const SeoData = ({ data, title, alias, canonical }) => {
    const router = useRouter();

    const { lang, seo, activeLangs } = useContext(UserContext);

    const validatedTitle = useMemo(
        () => (typeof title === 'string' ? title : title?.props?.defaultStr ? title?.props?.defaultStr : ''),
        [title]
    );
    const seoData = useMemo(() => data, [data]);

    const seoDataDefault = useMemo(() => {
        if (alias) {
            const seoDataAlias = seo?.find((s) => s?.alias === alias);
            if (
                !seoDataAlias?.seo_title?.[lang] &&
                !seoDataAlias?.seo_description?.[lang] &&
                !seoDataAlias?.seo_keywords?.[lang] &&
                !seoDataAlias?.seo_image?.[lang]
            ) {
                return seo?.find((s) => s.alias === 'default');
            } else {
                return seoDataAlias;
            }
        } else {
            return seo?.find((s) => s.alias === 'default');
        }
    }, [alias, lang, seo]);
    const meta = useMemo(() => {
        if (!seoDataDefault) return null;
        const accum = {
            title: null,
            description: null,
            keywords: null,
            seo_image: null
        };
        if (alias) {
            const seoDataAlias = seo?.find((s) => s?.alias === alias);
            if (seoDataAlias?.seo_title?.[lang]) {
                accum.title = seoDataDefault.seo_title[lang];
            }
        }

        if (seoDataDefault.seo_description && typeof seoDataDefault.seo_description === 'object') {
            accum.description = seoDataDefault.seo_description?.[lang] || null;
        } else {
            accum.description = seoDataDefault.seo_description;
        }
        if (seoDataDefault.seo_keywords && typeof seoDataDefault.seo_keywords === 'object') {
            accum.keywords = seoDataDefault.seo_keywords?.[lang] || null;
        } else {
            accum.keywords = seoDataDefault.seo_keywords;
        }
        if (seoDataDefault?.seo_image && typeof seoDataDefault?.seo_image === 'object') {
            accum.seo_image =
                typeof seoDataDefault?.seo_image?.[lang] === 'string'
                    ? seoDataDefault?.seo_image?.[lang]
                    : typeof seoDataDefault?.seo_image?.[lang]?.url === 'string'
                    ? seoDataDefault?.seo_image?.[lang]?.url
                    : null;
        } else {
            accum.seo_image = seoDataDefault?.seo_image;
        }
        return accum;
    }, [seoDataDefault, alias, seo, lang]);

    const metaDefault = useMemo(() => {
        const defaultData = seo?.find((s) => s.alias === 'default');
        if (!defaultData) return null;
        const accum = {
            title: null,
            description: null,
            keywords: null,
            seo_image: null
        };
        if (defaultData.seo_title && typeof defaultData.seo_title === 'object') {
            accum.title = defaultData?.seo_title?.[lang] || null;
        } else {
            accum.title = defaultData.seo_title;
        }
        if (defaultData.seo_description && typeof defaultData.seo_description === 'object') {
            accum.description = defaultData.seo_description?.[lang] || null;
        } else {
            accum.description = defaultData.seo_description;
        }
        if (defaultData.seo_keywords && typeof defaultData.seo_keywords === 'object') {
            accum.keywords = defaultData.seo_keywords?.[lang] || null;
        } else {
            accum.keywords = defaultData.seo_keywords;
        }
        if (defaultData?.seo_image && typeof defaultData?.seo_image === 'object') {
            accum.seo_image =
                typeof defaultData?.seo_image?.[lang] === 'string'
                    ? defaultData?.seo_image?.[lang]
                    : typeof defaultData?.seo_image?.[lang]?.url === 'string'
                    ? defaultData?.seo_image?.[lang]?.url
                    : null;
        } else {
            accum.seo_image = defaultData?.seo_image;
        }
        return accum;
    }, [lang, seo]);

    const metaTitle = useMemo(() => {
        const seo_title = typeof seoData?.seo_title === 'string' ? seoData?.seo_title : false;
        if (!seo_title && !meta?.title && !metaDefault?.title) {
            // Не вказано нічого = Назва сторінки
            return validatedTitle || '';
        } else if (!meta?.title && !metaDefault?.title) {
            // Не вказано заголовок по аліасу і за замовчуванням = Заголовок сторінки
            return seo_title;
        } else if (!seo_title && !metaDefault?.title) {
            // Не вказано заголовок сторінки і за замовчуванням = Заголовок по аліасу
            return `${meta?.title}`;
        } else if (!seo_title && !meta?.title) {
            // Не вказано заголовок сторінки і заголовок по аліасу = Назва сторінки - за замовчуванням
            return validatedTitle ? `${validatedTitle} - ${metaDefault?.title}` : metaDefault?.title;
        } else if (!seo_title) {
            // Не вказано заголовок сторінки = Заголовок по аліасу - за замовчуванням
            return `${meta?.title} - ${metaDefault?.title}`;
        } else {
            // Не вказано заголовок по аліасу або вказано все = Заголовок сторінки - за замовчуванням
            return `${seo_title} - ${metaDefault?.title}`;
        }
    }, [seoData?.seo_title, validatedTitle, meta?.title, metaDefault?.title]);

    const metaDescription = useMemo(
        () =>
            typeof seoData?.seo_description === 'string'
                ? seoData?.seo_description
                : typeof seoData?.seo_description?.[lang] === 'string'
                ? seoData?.seo_description?.[lang]
                : meta?.description || metaDefault?.description,
        [seoData?.seo_description, lang, meta?.description, metaDefault?.description]
    );

    const metaKeyWords = useMemo(
        () =>
            typeof seoData?.seo_keywords === 'string'
                ? seoData?.seo_keywords
                : typeof seoData?.seo_keywords?.[lang] === 'string'
                ? seoData?.seo_keywords?.[lang]
                : meta?.keywords || metaDefault?.keywords,
        [seoData?.seo_keywords, lang, meta?.keywords, metaDefault?.keywords]
    );

    const metaImage = useMemo(
        () =>
            typeof seoData?.seo_image === 'string'
                ? seoData?.seo_image
                : typeof seoData?.seo_image?.links?.preview === 'string'
                ? seoData.seo_image.links.preview
                : typeof seoData?.seo_image === 'string'
                ? seoData?.seo_image
                : typeof seoData?.seo_image?.[lang] === 'string'
                ? seoData?.seo_image?.[lang]
                : typeof seoData?.seo_image?.[lang]?.url === 'string'
                ? seoData?.seo_image?.[lang]?.url
                : meta?.seo_image || metaDefault?.seo_image,
        [seoData?.seo_image, lang, meta?.seo_image, metaDefault?.seo_image]
    );

    const defaultLang = useMemo(() => activeLangs.find((language) => language.isDefault) || {}, [activeLangs]);
    const isMainCopy =
        (getRoute(router?.asPath) === `/` && getLang(router?.asPath) === defaultLang.alias) ||
        (project.rootSeoUrl &&
            getRoute(router?.asPath) === project.rootSeoUrl &&
            (getLang(router?.asPath) === defaultLang.alias || !getLang(router?.asPath)));

    const isMainLangCopy = getLang(router?.asPath) === defaultLang.alias;
    const canonical_validated = isMainCopy
        ? `${SITE_URL}/`
        : isMainLangCopy
        ? `${SITE_URL}${getRoute(router.asPath) === '/' ? '' : getRoute(router.asPath)}`
        : canonical;

    return (
        <Head>
            {/* eslint-disable-next-line react/no-unknown-property */}
            <meta prefix='og: http://ogp.me/ns#' />
            {activeLangs?.map((l) => {
                return (
                    <link
                        key={`metaalternate${l.alias}${getRoute(router.asPath)}`}
                        rel='alternate'
                        href={`${SITE_URL}${l.isDefault ? '' : `/${l.alias}`}${
                            getRoute(router.asPath) === '/' ? '' : getRoute(router.asPath)
                        }`}
                        hrefLang={l.alias}
                    />
                );
            })}
            {process.env.NEXT_PUBLIC_NO_FOLLOW && <meta name='robots' content='noindex, nofollow' key='robots' />}
            <meta name='viewport' content='initial-scale=1.0, width=device-width' key='viewport' />
            {metaTitle ? (
                <>
                    <meta key='og:title' property='og:title' content={metaTitle} />
                    <title key='title'>{metaTitle}</title>
                </>
            ) : null}
            <meta key='og:site' property='og:site' content={project.company} />
            <meta key='og:site_name' property='og:site_name' content={project.company} />
            {metaDescription ? (
                <>
                    <meta key='description' name='description' content={metaDescription} />
                    <meta key='og:description' property='og:description' content={metaDescription} />
                </>
            ) : null}
            {metaKeyWords ? <meta key='keywords' name='keywords' content={metaKeyWords} /> : null}
            {canonical_validated ? (
                <>
                    <meta key='og:url' property='og:url' content={canonical_validated} />
                    <link key='canonical' rel='canonical' href={canonical_validated} />
                </>
            ) : null}
            {metaImage ? (
                <>
                    <meta property='image' content={metaImage} />
                    <meta property='og:image' content={metaImage} />
                </>
            ) : null}
        </Head>
    );
};

SeoData.propTypes = {
    data: PropTypes.object,
    title: PropTypes.string,
    canonical: PropTypes.string
};

export default SeoData;
